import * as React from "react";
import { Link } from "react-router-dom";
import { api, answeredIn, closesOn, revealRows, asLimitError, isSeriesIdValid, normalizeSeriesId, asRoundRefusal, asSessionOver, type DeriveSpec, type QuestionConfig, type QuestionAggregate, type QuestionType, type LimitError, type McOption, type PickMode, type QuestionVariant, type ScoringConfig, type ScoringMode, type TeamConfig, type TeamSet, type TeamStanding } from "../api.js";
import { t, getLocale } from "../i18n.js";
import { groupErrorKey, groupRows, type ResponseGroupView } from "../group.js";
import { Shell, McTally, LiveRegion, LimitBanner, MeanNote, SessionOverNotice, QrSvg, OmittedNote, OpenEntryText, WithheldNote } from "../components.js";
import { describeAggregate, describeLeaderboard } from "../announce.js";
import { useRoom, advertisableCode } from "../useRoom.js";

function uid(): string {
  return crypto.randomUUID();
}

/**
 * Apply the host's scoring choice to a scored mc round. Self-paced MEANS no clock, and the API
 * refuses the contradiction, so the time limit goes with it.
 */
function withScoring(q: QuestionConfig, scoring: ScoringConfig): QuestionConfig {
  const out: QuestionConfig = { ...q, scoring };
  if (scoring.mode === "self-paced") delete out.timeLimitMs;
  return out;
}

/** HITL AI panel: generates quiz drafts; the host opens a chosen draft manually. */
function AiDraftPanel({ sessionId, drafts, setDrafts, scoring }: {
  sessionId: string;
  drafts: QuestionConfig[];
  setDrafts: React.Dispatch<React.SetStateAction<QuestionConfig[]>>;
  scoring: ScoringConfig;
}): React.ReactElement {
  const [topic, setTopic] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [unavailable, setUnavailable] = React.useState(false);

  const generate = async (): Promise<void> => {
    if (!topic.trim()) return;
    setBusy(true);
    const { status, data } = await api.aiQuiz(topic.trim(), 3, getLocale());
    setBusy(false);
    if (status === 503) return setUnavailable(true);
    if (status === 200 && data.drafts) setDrafts(data.drafts);
  };

  if (unavailable) return <></>;
  return (
    <details className="rounded-md border border-border p-3">
      <summary className="cursor-pointer font-bold">{t("presenter.aiTitle")}</summary>
      <p className="my-2 text-sm text-muted">{t("presenter.aiHint")}</p>
      <div className="flex gap-2">
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("presenter.aiTopic")} className="flex-1 rounded-md border border-border p-2" />
        <button onClick={() => void generate()} disabled={busy} className="rounded-md bg-secondary px-3 font-bold text-secondary-foreground disabled:opacity-50">
          {t("presenter.aiGenerate")}
        </button>
      </div>
      {drafts.map((d) => (
        <div key={d.id} className="mt-2 flex items-center justify-between rounded-md bg-surface p-2">
          <span className="text-sm">{d.prompt}</span>
          <button
            onClick={() => {
              // An AI-drafted quiz is a quiz: the host's scoring choice applies here as everywhere else.
              void api.openQuestion(sessionId, withScoring({ ...d, id: uid() }, scoring));
              setDrafts((prev) => prev.filter((x) => x.id !== d.id));
            }}
            className="rounded-md bg-primary px-3 py-1 text-primary-foreground text-sm"
          >
            {t("presenter.aiOpen")}
          </button>
        </div>
      ))}
    </details>
  );
}

/** Fallback rounds so a race is runnable without the AI draft (offline, or AI unavailable). */
function presetRace(): QuestionConfig[] {
  const round = (prompt: string, labels: [string, string, string], correct: 0 | 1 | 2): QuestionConfig => ({
    id: uid(),
    type: "mc",
    prompt,
    scored: true,
    timeLimitMs: 20_000,
    options: labels.map((label, i) => ({ id: `o${i}`, label })),
    correctOptionIds: [`o${correct}`],
  });
  return [
    round(t("presenter.racePreset1"), ["1", "2", "3"], 1),
    round(t("presenter.racePreset2"), ["Brasília", "Rio de Janeiro", "São Paulo"], 0),
    round(t("presenter.racePreset3"), ["7", "8", "9"], 0),
  ];
}

/**
 * Host controls for a trivia race: load the rounds once, then advance. The server owns the clock and
 * closes each round on its deadline, so the host only decides WHEN to move on — and even if the host
 * stops, the last round auto-ends the race.
 */
function RacePanel({ sessionId, race, drafts, scoring }: {
  sessionId: string;
  race: { round: number; total: number; ended: boolean } | null;
  drafts: QuestionConfig[];
  scoring: ScoringConfig;
}): React.ReactElement {
  const [loaded, setLoaded] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const start = async (): Promise<void> => {
    // Prefer the host's AI-generated drafts; fall back to presets so this always works.
    // The API requires every race round to be timed — except a self-paced one, which withScoring
    // strips the clock from (the host's "next" closes it instead).
    const rounds = (drafts.length > 0 ? drafts : presetRace()).map((q) =>
      withScoring({ ...q, id: uid(), scored: true, timeLimitMs: q.timeLimitMs ?? 20_000 }, scoring),
    );
    const { status, data } = await api.loadRace(sessionId, rounds);
    if (status !== 201) return setError(data.error ?? String(status));
    setError(null);
    setLoaded(true);
    await api.nextRound(sessionId); // open round 1
  };

  const isLast = race !== null && race.round >= race.total;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-display-sm">{t("presenter.raceStart")}</h3>
        {race && (
          <span className="text-sm font-bold">
            {race.ended ? t("presenter.raceEnded") : `${t("presenter.raceRound")} ${race.round}/${race.total}`}
          </span>
        )}
      </div>
      <p className="text-sm text-muted">{t("presenter.raceHint")}</p>
      {error && <p className="text-sm font-bold text-warning">{error}</p>}
      {/* `race` is the SERVER's word (replayed on connect), `loaded` only this tab's: a host who
          reloaded mid-race still gets Next — the only way a self-paced race ends. */}
      {!loaded && !race && (
        <button onClick={() => void start()} className="rounded-md bg-primary p-3 font-bold text-primary-foreground">
          {t("presenter.raceStart")}
        </button>
      )}
      {(loaded || race) && !race?.ended && (
        <button onClick={() => void api.nextRound(sessionId)} className="rounded-md bg-secondary p-3 font-bold text-secondary-foreground">
          {isLast ? t("presenter.raceFinish") : t("presenter.raceNext")}
        </button>
      )}
    </div>
  );
}

/**
 * The host's live mc panel. Narrowed once here instead of re-narrowing `state.aggregate` per prop.
 *
 * ponytail: a withheld tally is withheld from the HOST too. One aggregate is broadcast to every
 * socket, so a host-only tally needs per-role targeting (roadmap W6) — until then, "the host sees
 * what the room sees" is the honest behaviour rather than a leak dressed up as a feature.
 */
function McPanel({
  prompt,
  options,
  agg,
}: {
  prompt: string;
  /** `McOption[]`, not `{id,label}[]`: a scale round's `value`s ride here and the mean needs them. */
  options: McOption[];
  agg: Extract<QuestionAggregate, { type: "mc" }>;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="font-display text-display-sm">{prompt}</h3>
      {agg.tally === "hidden" ? (
        <>
          {options.map((o) => (
            <div key={o.id} className="rounded-md bg-surface p-2 text-sm">{o.label}</div>
          ))}
          <p className="text-sm text-muted">
            {agg.total} {t(agg.total === 1 ? "mc.answeredOne" : "mc.answered")} · {t("mc.tallyHidden")}
          </p>
        </>
      ) : (
        <>
          <McTally options={options} agg={agg} />
          {agg.payload !== undefined && (
            <p className="text-sm text-muted">
              {t("prio.unit")} · {agg.total} {t(agg.total === 1 ? "mc.answeredOne" : "mc.answered")}
            </p>
          )}
          {/* The question frame's own table — the live aggregate has none (round-1 review). */}
          <MeanNote agg={agg} options={options} className="text-sm text-muted" />
        </>
      )}
    </div>
  );
}

/**
 * Host view of a prompt-vote round. The host sees a count while people write — deliberately not the
 * answers, so nobody can claim the facilitator screened them — then the ballot, where Hide pulls an
 * entry off the projection and out of the score. Moderation here is structurally post-hoc: an abusive
 * answer is on screen the moment voting opens, so this format needs a present, briefed host.
 */
/**
 * Q&A review queue. The room's broadcast carries only a COUNT of entries in review — the text lands
 * here through an authenticated host read, so nothing unapproved is ever on a participant's phone or
 * the projector. Refetched when the count changes, which is the only signal that matters.
 */
function ReviewQueue({ sessionId, pendingCount }: { sessionId: string; pendingCount: number }): React.ReactElement {
  const [queue, setQueue] = React.useState<Array<{ id: string; text: string }>>([]);
  const [refused, setRefused] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    void api.pending(sessionId).then((entries) => {
      if (live) setQueue(entries);
    });
    return () => {
      live = false;
    };
  }, [sessionId, pendingCount]);

  const decide = async (entryId: string, approved: boolean): Promise<void> => {
    // Drop it locally first: the aggregate that carries the new count arrives over the socket, and
    // leaving a decided entry on screen until then reads as a dead button.
    setQueue((prev) => prev.filter((e) => e.id !== entryId));
    const { status } = await api.moderate(sessionId, entryId, { approved });
    // 409 = the ROUND refused the change (round-3 review): approving an entry another host had just
    // dismissed would leave two of that author's on a `maxEntries: 1` wall. This used to be read as
    // success, so the row left the queue and the host believed they had published a question that is
    // still hidden. Re-read the queue rather than push the row back, which is what makes the list the
    // server's again — in the server's order — instead of the optimism's.
    setRefused(status === 409);
    if (status === 409) setQueue(await api.pending(sessionId));
  };

  if (pendingCount === 0 && queue.length === 0) return <></>;
  return (
    <div className="flex flex-col gap-2 rounded-md border border-warning p-3">
      <h3 className="font-display text-display-sm">
        {t("review.title")} ({pendingCount})
      </h3>
      <p className="text-sm text-muted">{t("review.hint")}</p>
      {refused && (
        <p role="alert" className="text-sm font-bold text-warning">
          {t("error.MODERATION_REFUSED")}
        </p>
      )}
      {queue.map((e) => (
        <div key={e.id} className="flex items-center justify-between gap-2 rounded-md bg-surface p-2">
          <span className="text-sm">{e.text}</span>
          <span className="flex gap-1">
            <button
              onClick={() => void decide(e.id, true)}
              className="rounded-md bg-success px-3 py-1 text-sm font-bold text-primary-foreground"
            >
              {t("review.approve")}
            </button>
            <button
              onClick={() => void decide(e.id, false)}
              className="rounded-md bg-warning px-3 py-1 text-sm font-bold text-primary-foreground"
            >
              {t("review.dismiss")}
            </button>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * "Agrupar por IA" (US-LV-21). One click, one request: nothing runs until the host presses the
 * button, and the categories live in this component's state only — never on the projection, never
 * stored. The texts shown come from the aggregate this screen already has.
 */
function GroupPanel({
  sessionId,
  entries,
}: {
  sessionId: string;
  entries: Array<{ id: string; text: string }>;
}): React.ReactElement {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [result, setResult] = React.useState<{ groups: ResponseGroupView[]; skipped: number } | null>(null);

  const run = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const { status, data } = await api.groupOpen(sessionId, getLocale());
      if (status === 200 && data.groups) setResult({ groups: data.groups, skipped: data.skipped ?? 0 });
      else setError(groupErrorKey(status, data.error));
    } catch {
      setError(groupErrorKey(0, undefined));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <button
        onClick={() => void run()}
        disabled={busy}
        className="self-start rounded-md bg-primary px-3 py-1 text-sm font-bold text-primary-foreground disabled:opacity-60"
      >
        {busy ? t("grp.working") : t("grp.button")}
      </button>
      <p className="text-sm text-muted">{t("grp.hint")}</p>
      {error && (
        <p role="alert" className="text-sm font-bold text-warning">
          {t(error)}
        </p>
      )}
      {result && (
        <div className="flex flex-col gap-2">
          <h3 className="font-display text-display-sm">{t("grp.title")}</h3>
          {result.skipped > 0 && <p className="text-sm text-muted">{t("grp.skipped", { n: String(result.skipped) })}</p>}
          {groupRows(result.groups, entries).map((g) => (
            <div key={g.label} className="rounded-md bg-surface p-2">
              <b>{g.label}</b> ({g.count})
              <ul className="list-disc pl-5 text-sm">
                {g.texts.map((text, i) => (
                  <li key={i}>{text}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PromptVoteHostPanel({
  sessionId,
  aggregate,
}: {
  sessionId: string;
  aggregate: Extract<QuestionAggregate, { type: "promptvote" }>;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <h3 className="font-display text-display-sm">{t("pv.title")}</h3>
      {aggregate.phase === "write" && (
        <p className="text-body-lg">
          {aggregate.submissions} {t(aggregate.submissions === 1 ? "pv.submissionOne" : "pv.submissions")}
        </p>
      )}
      {aggregate.phase === "vote" && (
        <>
          <p className="text-sm text-muted">
            {aggregate.votesCast} {t("pv.votesCast")}
          </p>
          <OmittedNote n={aggregate.omitted} />
          {aggregate.ballot.map((entry) => (
            <div key={entry.id} className="flex items-center justify-between gap-2 rounded-md bg-surface p-2">
              <span className="text-sm">{entry.text}</span>
              <button
                onClick={() => void api.moderate(sessionId, entry.id, { hidden: true })}
                className="rounded-md bg-warning px-3 py-1 text-sm font-bold text-primary-foreground"
              >
                {t("pv.hide")}
              </button>
            </div>
          ))}
        </>
      )}
      {aggregate.phase === "reveal" && <OmittedNote n={aggregate.omitted} />}
      {aggregate.phase === "reveal" &&
        revealRows(aggregate).map((r) => (
          <div key={r.id} className="flex justify-between rounded-md bg-surface p-2">
            <span className="text-sm">
              {r.text}
              {/* An anonymous round carries no nickname on this arm at all — the host does not get a
                  back channel to the authors the room was promised would stay hidden. */}
              {r.author !== null && <> — <b>{r.author}</b></>}
            </span>
            <span className="font-bold">
              {r.votes} {t(r.votes === 1 ? "pv.voteOne" : "pv.votes")}
            </span>
          </div>
        ))}
    </div>
  );
}

/** A spin long enough to build the moment, short enough that the room does not start talking. */
const SPIN_MS = 4_000;

/**
 * Client-side twin of the API's picker checks, so a bad box is a message on THIS screen instead of a
 * silent 400. `k` is only judged for `teams`; the other three draws ignore that box.
 */
export function pickerInput(
  pick: PickMode,
  pool: string,
  teams: string,
): { names: string[]; k: number } | { error: "picker.badTeams" | "picker.badPool" } {
  const k = Number(teams);
  if (pick === "teams" && !(Number.isInteger(k) && k >= 2 && k <= 20)) return { error: "picker.badTeams" };
  // One name per line, blanks dropped.
  const names = pool.split("\n").map((n) => n.trim()).filter((n) => n.length > 0);
  if (names.length > 100 || names.some((n) => n.length > 48)) return { error: "picker.badPool" };
  return { names, k };
}

/**
 * The host's draw panel ("Sorteio", US-LV-6).
 *
 * Four buttons because there are four draws, not because there are four settings — the whole point
 * of W5 is that a facilitator picks the OUTCOME they want ("someone who hasn't spoken", "split us
 * up", "give us an order") rather than configuring a wheel.
 *
 * The result appears here only once the round is `drawn`. The host does not get a preview: this
 * screen is fed by the same broadcast as the projector, deliberately (see the server's aggregate).
 */
function PickerPanel({ sessionId, aggregate, onResult }: {
  sessionId: string;
  aggregate: Extract<QuestionAggregate, { type: "picker" }> | null;
  onResult: (res: { status: number; data: unknown }) => void;
}): React.ReactElement {
  const [teams, setTeams] = React.useState("2");
  const [pool, setPool] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  const fromList = pool.trim().length > 0;

  const draw = async (pick: PickMode): Promise<void> => {
    const input = pickerInput(pick, pool, teams);
    if ("error" in input) {
      setErr(t(input.error));
      return;
    }
    setErr(null);
    const config: QuestionConfig = { id: uid(), type: "picker", prompt: t("picker.title"), pick, timeLimitMs: SPIN_MS };
    if (pick === "teams") config.k = input.k;
    // An empty box means "draw from the room", the common case, which needs no toggle to say so.
    if (input.names.length > 0) config.pool = input.names;
    const res = await api.openQuestion(sessionId, config);
    // A 400 is the host's own input (the API mirrors pickerInput, plus names that look like a phone
    // or CPF, which the client does not judge). Say so — a click that does nothing gets clicked again.
    if (res.status === 400) {
      setErr(t("picker.failed", { code: String((res.data as { error?: string } | null)?.error ?? 400) }));
    }
    onResult(res);
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <h3 className="font-display text-display-sm">🎲 {t("picker.title")}</h3>
      <p className="text-sm text-muted">{t("picker.spinHint")}</p>
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => void draw("one")} className="rounded-md bg-surface p-3 font-bold">{t("picker.one")}</button>
        {/* A typed list has no participation history, so "who took part least" would silently be a
            plain draw — disabled rather than mislabelled. */}
        <button
          onClick={() => void draw("weighted")}
          disabled={fromList}
          title={fromList ? t("picker.weightedRoomOnly") : undefined}
          className="rounded-md bg-surface p-3 font-bold disabled:opacity-50"
        >
          {t("picker.weighted")}
        </button>
        <button onClick={() => void draw("rotation")} className="rounded-md bg-surface p-3 font-bold">{t("picker.rotation")}</button>
        <div className="flex items-stretch gap-2">
          <button onClick={() => void draw("teams")} className="flex-1 rounded-md bg-surface p-3 font-bold">{t("picker.teams")}</button>
          <label className="flex items-center gap-1 text-sm">
            <span className="sr-only">{t("picker.teamCount")}</span>
            <input
              type="number"
              min={2}
              max={20}
              value={teams}
              onChange={(e) => setTeams(e.target.value)}
              aria-label={t("picker.teamCount")}
              className="w-14 rounded-md border border-border p-2 text-center"
            />
          </label>
        </div>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("picker.pool")}</span>
        <textarea
          value={pool}
          onChange={(e) => setPool(e.target.value)}
          placeholder={t("picker.poolPlaceholder")}
          rows={3}
          className="rounded-md border border-border p-2"
        />
      </label>
      {err && (
        <p role="alert" className="text-sm font-bold text-accent">
          {err}
        </p>
      )}
      {aggregate?.phase === "spinning" && <p className="font-bold text-accent">{t("picker.spinning")}</p>}
      {aggregate?.phase === "drawn" && (
        <div className="flex flex-col gap-1 rounded-md bg-surface p-3">
          <span className="text-sm text-muted">{t("picker.drawn")}</span>
          {aggregate.pick === "teams" &&
            aggregate.teams.map((team, i) => (
              <span key={i} className="font-bold">
                {t("picker.team")} {i + 1}: {team.join(", ")}
              </span>
            ))}
          {aggregate.pick === "rotation" && (
            <span className="font-bold">{aggregate.order.map((n, i) => `${i + 1}. ${n}`).join(" · ")}</span>
          )}
          {aggregate.pick !== "teams" && aggregate.pick !== "rotation" && (
            <span className="font-display text-display-sm">{aggregate.drawn ?? t("picker.nobody")}</span>
          )}
        </div>
      )}
    </div>
  );
}

/** Mirrors the API's `MAX_VARIANT_PROMPT`. A private brief is read on a phone, under a table. */
const MAX_BRIEF = 500;

/**
 * Turn the Alvo panel's state into the round's targeting table (W6a, US-LV-13).
 *
 * Pure and exported for the same reason `pickerInput` is: the rule that decides whether a round is
 * targeted at all is worth a test, and it must not need a DOM to run.
 *
 * `null` variants means "not a targeted round" — and that is deliberately what BOTH half-filled
 * states produce. People with no brief is a no-op; a brief addressed to nobody is a brief the host
 * believes they sent. The panel shows the recipient count so neither is a surprise.
 */
export function variantFrom(pids: string[], brief: string): { variants: QuestionVariant[] | null } | { error: "target.tooLong" } {
  const prompt = brief.trim();
  if (prompt.length > MAX_BRIEF) return { error: "target.tooLong" };
  if (pids.length === 0 || prompt.length === 0) return { variants: null };
  return { variants: [{ pids, prompt }] };
}

/**
 * "🎯 Alvo" — who receives a different version of the next round.
 *
 * The roster is an authenticated host read, refetched whenever the room's headcount changes: pids
 * are deliberately off the wire, so the socket cannot tell the host who just arrived. That is the
 * same trade the Q&A review queue makes, and for the same reason.
 */
function TargetPanel({ sessionId, headcount, pids, setPids, brief, setBrief, error }: {
  sessionId: string;
  headcount: number;
  pids: string[];
  setPids: React.Dispatch<React.SetStateAction<string[]>>;
  brief: string;
  setBrief: (next: string) => void;
  error: string | null;
}): React.ReactElement {
  const { roster, names, failed: rosterError } = useRoster(sessionId, headcount);
  // Everyone seated, plus every armed pid that is not — as a ghost row rather than a silent removal.
  // Pruning here used to un-arm a phone that dropped for three seconds, and the identity fix means
  // that same phone comes back with the same pid: it would return to a round it had been cut from.
  const rows = pickRows(pids, roster, names);

  return (
    <details className="rounded-md border border-border p-3">
      <summary className="cursor-pointer font-bold">🎯 {t("target.title")}</summary>
      <p className="my-2 text-sm text-muted">{t("target.hint")}</p>
      {rosterError && <p className="text-sm font-bold text-warning">{t("target.rosterError")}</p>}
      {rows.length === 0 ? (
        <p className="text-sm text-muted">{t("target.empty")}</p>
      ) : (
        <div className="grid grid-cols-2 gap-1">
          {rows.map((row) => (
            <PickCheckbox
              key={row.pid}
              row={row}
              armed={pids.includes(row.pid)}
              onToggle={() => setPids(pids.includes(row.pid) ? pids.filter((x) => x !== row.pid) : [...pids, row.pid])}
            />
          ))}
        </div>
      )}
      <label className="mt-2 flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("target.brief")}</span>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder={t("target.briefPlaceholder")}
          rows={3}
          maxLength={MAX_BRIEF}
          className="rounded-md border border-border p-2"
        />
      </label>
      {error && (
        <p role="alert" className="text-sm font-bold text-accent">
          {error}
        </p>
      )}
      {pids.length > 0 && (
        <p className="mt-2 text-sm font-bold">
          {t(pids.length === 1 ? "target.countOne" : "target.count", { n: String(pids.length) })}
        </p>
      )}
    </details>
  );
}

/** The API's ceiling; the panel offers 2..8, which is what fits a room and a projector. */
const MAX_TEAMS_UI = 8;

/**
 * Deal the roster into `k` balanced teams (W7, US-LV-14).
 *
 * Shuffle then round-robin, which is the whole algorithm: round-robin alone would seat people by join
 * order (the three who arrived together stay together, and the last-minute arrivals become "the team
 * that is always late"), and shuffle alone would leave sizes lopsided. Together, every team differs in
 * size by at most one and nobody chose who they are with.
 *
 * Pure and exported for the same reason `pickerInput` and `variantFrom` are: it is a rule worth a
 * test, and it must not need a DOM to run.
 */
export function splitTeams(roster: ReadonlyArray<{ pid: string; nickname: string }>, k: number): TeamConfig[] {
  const shuffled = [...roster];
  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }
  return Array.from({ length: k }, (_, i) => ({
    id: `t${i + 1}`,
    name: t("teams.defaultName", { n: String(i + 1) }),
    pids: shuffled.filter((_, idx) => idx % k === i).map((p) => p.pid),
  }));
}

/** One checkbox in a host panel: someone in the room, or an armed pid whose phone is not. */
export interface PickRow {
  pid: string;
  nickname: string;
  /** False = a GHOST: armed, but not on the roster this instant. Still ticked, still untickable. */
  present: boolean;
}

/**
 * The rows a host panel should show (round-2 review, fix 1).
 *
 * Pruning armed pids against the seat roster was a REGRESSION, and the identity fix is what made it
 * one: the roster is refetched on every `joined`/`left`, so a phone that dropped for three seconds
 * lost its pid from the floor — and then came back holding the SAME pid, now missing from the list,
 * and was refused for the rest of the round. Silent removal is the wrong answer twice over: it
 * discards a host's deliberate choice, and it does so at the exact moment the person is coming back.
 *
 * So nothing is removed. An armed pid that is not seated right now renders as its own row, ticked,
 * with the last name we saw for it — the host can see it, and can untick it if they mean to. That is
 * the whole rule: nothing disappears without the host, and nothing becomes impossible to untick.
 */
export function pickRows(
  armed: readonly string[],
  roster: ReadonlyArray<{ pid: string; nickname: string }>,
  names: ReadonlyMap<string, string>,
): PickRow[] {
  const rows: PickRow[] = roster.map((r) => ({ pid: r.pid, nickname: r.nickname, present: true }));
  for (const pid of armed) {
    if (rows.some((r) => r.pid === pid)) continue;
    // The pid itself is the last resort — a host who never saw this person still gets a row they can
    // untick, which beats an invisible entry that keeps scoping their rounds.
    rows.push({ pid, nickname: names.get(pid) ?? pid, present: false });
  }
  return rows;
}

/**
 * The host's roster, plus every name this panel has EVER seen.
 *
 * One hook because both panels need exactly this and both got it subtly wrong on their own: the
 * roster read can fail (a 401 after the host session expires), and a failed read is not an empty
 * room — the Alvo panel said so and the Times panel silently pretended nobody had joined.
 *
 * `names` accumulates and is never pruned: it is what turns a departed pid into a readable ghost, and
 * a room's worth of nicknames is nothing. It is a ref, not state, because it changes only alongside
 * `roster`, which already re-renders.
 */
function useRoster(sessionId: string, headcount: number): {
  roster: Array<{ pid: string; nickname: string }>;
  names: ReadonlyMap<string, string>;
  failed: boolean;
} {
  const [roster, setRoster] = React.useState<Array<{ pid: string; nickname: string }>>([]);
  const [failed, setFailed] = React.useState(false);
  const names = React.useRef(new Map<string, string>());

  React.useEffect(() => {
    let live = true;
    void api.participants(sessionId).then((rows) => {
      if (!live) return;
      // null = the read failed. Keep the last roster and SAY so — an empty list here reads as
      // "nobody joined" while forty people are visibly seated.
      if (rows === null) return setFailed(true);
      setFailed(false);
      for (const row of rows) names.current.set(row.pid, row.nickname);
      setRoster(rows);
    });
    return () => {
      live = false;
    };
  }, [sessionId, headcount]);

  return { roster, names: names.current, failed };
}

/** One armed/unarmed checkbox, with the "fora da sala" suffix a ghost carries. */
function PickCheckbox({ row, armed, onToggle }: {
  row: PickRow;
  armed: boolean;
  onToggle: () => void;
}): React.ReactElement {
  return (
    <label className={`flex items-center gap-2 rounded-md p-2 text-sm ${row.present ? "bg-surface" : "border border-warning"}`}>
      <input type="checkbox" checked={armed} onChange={onToggle} />
      <span>
        {row.nickname}
        {!row.present && <span className="text-muted"> — {t("roster.away")}</span>}
      </span>
    </label>
  );
}

/**
 * The members of one team, as the host's own screen should show them (W7 review, LOW-8).
 *
 * `secret` has exactly one honest meaning, and it is the one the panel's own hint promises: it
 * changes THIS screen, never the wire. A host laptop is routinely mirrored to the projector, so a
 * room running hidden roles cannot have the membership sitting in an open panel — while a room NOT
 * running them very much wants to see who is on which team.
 *
 * Pure and exported so the branch is a test rather than a screenshot. Unknown pids are dropped: the
 * set can name someone who has since left, and rendering "undefined" beside three real names is
 * worse than rendering two.
 */
export function memberLabel(pids: readonly string[], names: ReadonlyMap<string, string>, hidden: boolean): string {
  if (hidden) return t("teams.membersHidden");
  if (pids.length === 0) return t("teams.noMembers");
  // Last-known names, not the current seat roster: a team does not shrink because a phone dropped,
  // and reading the roster here made this line contradict the size label beside it ("2 pessoas —
  // Ana"). If any member is one this panel has never seen, fall back to the count alone rather than
  // printing a partial list that contradicts it again.
  const known = pids.map((pid) => names.get(pid)).filter((n): n is string => n !== undefined);
  if (known.length < pids.length) return t(pids.length === 1 ? "teams.sizeOne" : "teams.size", { n: String(pids.length) });
  return known.join(", ");
}

/**
 * "👥 Times" — the team layer, plus the floor control that decides who may answer the next round.
 *
 * One panel and one roster read, because both controls address the same thing: the public ids the
 * host can see and the room cannot. The roster is refetched when the headcount changes, exactly as
 * the Alvo panel does, and for the same reason — pids are off the wire, so the socket cannot tell the
 * host who just walked in.
 */
function TeamsPanel({ sessionId, headcount, standings, eligible, setEligible }: {
  sessionId: string;
  headcount: number;
  standings: TeamStanding[];
  eligible: string[];
  setEligible: React.Dispatch<React.SetStateAction<string[]>>;
}): React.ReactElement {
  const { roster, names, failed: rosterError } = useRoster(sessionId, headcount);
  const [draft, setDraft] = React.useState<TeamConfig[]>([]);
  const [k, setK] = React.useState(2);
  const [aggregate, setAggregate] = React.useState<"sum" | "mean">("sum");
  const [secret, setSecret] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);
  // Whether the SAVED set is secret — deliberately not the checkbox beside it. A host ticking the box
  // while building teams is still building them, and blanking the names they are checking mid-edit
  // would make the panel unusable. It takes effect once the room is actually running that way.
  const [hideMembers, setHideMembers] = React.useState(false);

  // Hydrate from the server ONCE per room: the teams are the host's own state, and a reload must not
  // land on an empty panel over a room that is physically sitting in teams. Deliberately not keyed to
  // `headcount` — refetching would overwrite an unsaved draft every time somebody joined.
  React.useEffect(() => {
    let live = true;
    void api.teams(sessionId).then((set) => {
      if (!live || !set) return; // undefined = the read failed; null = there are genuinely no teams
      setDraft(set.teams);
      setAggregate(set.aggregate);
      setSecret(set.secret);
      setHideMembers(set.secret);
      setK(set.teams.length);
    });
    return () => {
      live = false;
    };
  }, [sessionId]);

  const save = async (set: TeamSet | null): Promise<void> => {
    const { status, data } = await api.setTeams(sessionId, set);
    if (status !== 200) {
      setSaved(false);
      return setError(t("teams.failed", { code: String(data.error ?? status) }));
    }
    setError(null);
    setSaved(true);
    setHideMembers(set !== null && set.secret);
  };

  const toggle = (pid: string): void =>
    setEligible((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]));

  return (
    <details className="rounded-md border border-border p-3">
      <summary className="cursor-pointer font-bold">👥 {t("teams.title")}</summary>
      <p className="my-2 text-sm text-muted">{t("teams.hint")}</p>
      {/* A failed read is not an empty room. This panel used to swallow it and show "ninguém na sala
          ainda" to a host looking at forty people — the Alvo panel has always said so. */}
      {rosterError && <p className="text-sm font-bold text-warning">{t("target.rosterError")}</p>}
      {roster.length === 0 && !rosterError && <p className="text-sm text-muted">{t("teams.empty")}</p>}

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-sm">
          <span className="text-muted">{t("teams.count")}</span>
          <input
            type="number"
            min={2}
            max={MAX_TEAMS_UI}
            value={k}
            onChange={(e) => setK(Math.min(MAX_TEAMS_UI, Math.max(2, Number(e.target.value) || 2)))}
            aria-label={t("teams.count")}
            className="w-14 rounded-md border border-border p-2 text-center"
          />
        </label>
        <button
          onClick={() => {
            setDraft(splitTeams(roster, k));
            setSaved(false);
          }}
          disabled={roster.length === 0}
          className="rounded-md bg-surface px-3 py-2 font-bold disabled:opacity-50"
        >
          {t("teams.draw")}
        </button>
        <label className="flex items-center gap-1 text-sm">
          <span className="text-muted">{t("teams.aggregate")}</span>
          <select
            value={aggregate}
            onChange={(e) => setAggregate(e.target.value === "mean" ? "mean" : "sum")}
            className="rounded-md border border-border bg-surface px-2 py-1"
          >
            <option value="sum">{t("teams.sum")}</option>
            <option value="mean">{t("teams.mean")}</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm" title={t("teams.secretHint")}>
          <input type="checkbox" checked={secret} onChange={() => setSecret((v) => !v)} />
          <span>{t("teams.secret")}</span>
        </label>
      </div>

      {draft.map((team, i) => (
        <label key={team.id} className="mt-2 flex items-center gap-2 text-sm">
          <input
            value={team.name}
            onChange={(e) =>
              setDraft((prev) => prev.map((row) => (row.id === team.id ? { ...row, name: e.target.value } : row)))
            }
            aria-label={t("teams.name", { n: String(i + 1) })}
            maxLength={32}
            className="flex-1 rounded-md border border-border p-2"
          />
          <span className="text-muted">
            {t(team.pids.length === 1 ? "teams.sizeOne" : "teams.size", { n: String(team.pids.length) })}
          </span>
        </label>
      ))}
      {draft.length > 0 && (
        <ul className="mt-1 flex flex-col gap-1 text-sm text-muted">
          {draft.map((team) => (
            <li key={team.id}>
              <b>{team.name}</b>: {memberLabel(team.pids, names, hideMembers)}
            </li>
          ))}
        </ul>
      )}

      {draft.length > 0 && (
        <div className="mt-2 flex gap-2">
          <button
            onClick={() => void save({ aggregate, secret, teams: draft })}
            className="flex-1 rounded-md bg-primary p-2 font-bold text-primary-foreground"
          >
            {t("teams.save")}
          </button>
          <button
            onClick={() => {
              setDraft([]);
              setSaved(false);
              void save(null);
            }}
            className="rounded-md bg-surface px-3 py-2 font-bold"
          >
            {t("teams.clear")}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm font-bold text-accent">
          {error}
        </p>
      )}
      {saved && !error && <p className="mt-2 text-sm font-bold">{t("teams.saved")}</p>}

      {standings.length > 0 && (
        <div className="mt-3 flex flex-col gap-1">
          <h4 className="font-bold">{t("teams.standings")}</h4>
          {standings.map((row) => (
            <div key={row.id} className="flex justify-between rounded-md bg-surface p-2 text-sm">
              <span>
                {row.rank}. {row.name}{" "}
                <span className="text-muted">
                  ({t(row.size === 1 ? "teams.sizeOne" : "teams.size", { n: String(row.size) })})
                </span>
              </span>
              <span className="font-bold">{row.score}</span>
            </div>
          ))}
        </div>
      )}

      <h4 className="mt-3 font-bold">{t("teams.floorTitle")}</h4>
      <p className="my-1 text-sm text-muted">{t("teams.floorHint")}</p>
      {/* Whole-team shortcuts first: "the inner circle answers" is a team-shaped sentence, and
          ticking six checkboxes while a room waits is not. */}
      {draft.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {draft.map((team) => {
            const armed = team.pids.length > 0 && team.pids.every((pid) => eligible.includes(pid));
            return (
              <button
                key={team.id}
                onClick={() =>
                  setEligible((prev) =>
                    armed ? prev.filter((pid) => !team.pids.includes(pid)) : [...new Set([...prev, ...team.pids])],
                  )
                }
                aria-pressed={armed}
                className={`rounded-md px-3 py-1 text-sm font-bold ${armed ? "bg-accent text-primary-foreground" : "bg-surface"}`}
              >
                {team.name}
              </button>
            );
          })}
        </div>
      )}
      <div className="mt-2 grid grid-cols-2 gap-1">
        {pickRows(eligible, roster, names).map((row) => (
          <PickCheckbox key={row.pid} row={row} armed={eligible.includes(row.pid)} onToggle={() => toggle(row.pid)} />
        ))}
      </div>
      <p className="mt-2 text-sm font-bold">
        {eligible.length === 0
          ? t("teams.floorAll")
          : t(eligible.length === 1 ? "teams.floorCountOne" : "teams.floorCount", { n: String(eligible.length) })}
      </p>
    </details>
  );
}

/** The API's own bounds, mirrored: a bad box is a message on THIS screen instead of a silent 400. */
const PRIO_MIN_OPTIONS = 2;
const PRIO_MAX_OPTIONS = 10;
/** A ballot line lands on one projector row, the same place a participant answer does. */
const PRIO_LABEL_MAX = 120;

/** The budgets the panel offers. 100 is the classic 100-point allocation — "Orçamento fixo". */
const PRIO_BUDGETS = [5, 10, 100] as const;

/**
 * The host's priority round, built from the panel's boxes (W9a, US-LV-16).
 *
 * Pure and exported for the same reason `pickerInput` is: the rules that decide whether a round is
 * openable at all are worth a test, and they must not need a DOM to run.
 *
 * Option ids are POSITIONAL (`o0..on`), like every typed round: the labels are the host's own text,
 * and an id derived from it would change the moment they fixed a typo.
 */
export function priorityInput(
  prompt: string,
  options: string,
  mode: "weighted" | "ordered",
  budget: number,
): QuestionConfig | { error: "prio.badPrompt" | "prio.badOptions" } {
  const ask = prompt.trim();
  if (ask.length === 0) return { error: "prio.badPrompt" };
  // One per line, blanks dropped — the same shape the picker's pool box takes.
  const labels = options.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
  if (labels.length < PRIO_MIN_OPTIONS || labels.length > PRIO_MAX_OPTIONS) return { error: "prio.badOptions" };
  if (labels.some((l) => l.length > PRIO_LABEL_MAX)) return { error: "prio.badOptions" };
  const config: QuestionConfig = {
    id: uid(),
    type: "mc",
    prompt: ask,
    payload: mode,
    options: labels.map((label, i) => ({ id: `o${i}`, label })),
  };
  // A budget is what `weighted` MEANS, and the API refuses one beside a ranking.
  if (mode === "weighted") config.budget = budget;
  return config;
}

/**
 * "🎯 Priorização" — the three mechanics facilitators name by heart, as one panel.
 *
 * It is an `mc` opener like the preset buttons above, so it consumes the armed floor ("Quem
 * responde") and the armed brief ("Alvo") exactly as they do, and clears them only once the round
 * actually went out. A brief carrying OPTIONS is impossible from that panel and refused by the API
 * anyway (`VARIANT_OPTIONS_ON_PAYLOAD`) — a private subset would put one person's ranking out of 3
 * and everyone else's out of 8.
 */
function PriorityPanel({ sessionId, armed, onResult, onSent }: {
  sessionId: string;
  armed: { eligible: string[]; targetPids: string[]; brief: string };
  onResult: (res: { status: number; data: unknown }) => void;
  onSent: (sent: { eligible: boolean; variants: boolean }) => void;
}): React.ReactElement {
  const [prompt, setPrompt] = React.useState(() => t("prio.presetPrompt"));
  const [options, setOptions] = React.useState("");
  const [mode, setMode] = React.useState<"weighted" | "ordered">("weighted");
  const [budget, setBudget] = React.useState<number>(10);
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const open = async (): Promise<void> => {
    const built = priorityInput(prompt, options, mode, budget);
    if ("error" in built) return setErr(t(built.error));
    const targeting = variantFrom(armed.targetPids, armed.brief);
    if ("error" in targeting) return setErr(t(targeting.error));
    let config = built;
    if (armed.eligible.length > 0) config = { ...config, eligible: [...armed.eligible] };
    if (targeting.variants) config = { ...config, variants: targeting.variants };
    setErr(null);
    setBusy(true);
    const res = await api.openQuestion(sessionId, config);
    setBusy(false);
    setErr(
      res.status === 400
        ? t("prio.failed", { code: refusalText((res.data as { error?: string } | null)?.error, "prio") })
        : null,
    );
    onResult(res);
    // Clear EXACTLY what went out, and only once it did — the same rule the other openers follow.
    if (res.status === 201) {
      onSent({ eligible: config.eligible !== undefined, variants: config.variants !== undefined });
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <h3 className="font-display text-display-sm">🎯 {t("prio.title")}</h3>
      <p className="text-sm text-muted">{t("prio.hint")}</p>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("prio.prompt")}</span>
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          aria-label={t("prio.prompt")}
          className="rounded-md border border-border p-2"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("prio.options")}</span>
        <textarea
          value={options}
          onChange={(e) => setOptions(e.target.value)}
          placeholder={t("prio.optionsPlaceholder")}
          rows={4}
          className="rounded-md border border-border p-2"
        />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-sm">
          <span className="text-muted">{t("prio.mode")}</span>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as "weighted" | "ordered")}
            aria-label={t("prio.mode")}
            className="rounded-md border border-border bg-surface px-2 py-1"
          >
            <option value="weighted">{t("prio.modePoints")}</option>
            <option value="ordered">{t("prio.modeRank")}</option>
          </select>
        </label>
        {/* Only `weighted` has a budget to set: a ranking spends nothing. */}
        {mode === "weighted" && (
          <label className="flex items-center gap-1 text-sm">
            <span className="text-muted">{t("prio.budget")}</span>
            <select
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
              aria-label={t("prio.budget")}
              className="rounded-md border border-border bg-surface px-2 py-1"
            >
              {PRIO_BUDGETS.map((b) => (
                <option key={b} value={b}>
                  {b === 100 ? t("prio.budgetFixed") : t("prio.budgetPoints", { n: String(b) })}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {err && (
        <p role="alert" className="text-sm font-bold text-accent">
          {err}
        </p>
      )}
      <button
        onClick={() => void open()}
        disabled={busy}
        className="rounded-md bg-surface p-3 font-bold disabled:opacity-50"
      >
        {t("prio.open")}
      </button>
    </div>
  );
}

/** The scale ENDS the panel offers. 0–10 is the thermometer the seeded card already runs and 1–5
 *  is the agreement scale facilitators ask for by name; both ends are host-chosen, never inferred
 *  from the labels (spec assumption 1). */
const SCALE_FROM = [0, 1] as const;
const SCALE_TO = [5, 10] as const;
/** Eleven points (0..10) is the widest scale one projector row and one phone screen both hold. */
const SCALE_MAX_POINTS = 11;

/**
 * The host's scale round, built from the panel's boxes (W9b, US-LV-5).
 *
 * Pure and exported for the same reason `priorityInput` is: the rules that decide whether a round
 * is openable at all are worth a test, and they must not need a DOM to run.
 *
 * Ids are POSITIONAL (`o0..on`), like every typed round — the MEANING is in `value`, which is what
 * lets a 1–5 scale and a 0–10 one share a renderer, and what keeps the seeded termômetro's own
 * `o0..o10` from being renumbered by a positional rule.
 */
export function scaleInput(
  prompt: string,
  from: number,
  to: number,
): QuestionConfig | { error: "scale.badPrompt" | "scale.badRange" } {
  const ask = prompt.trim();
  if (ask.length === 0) return { error: "scale.badPrompt" };
  if (!Number.isInteger(from) || !Number.isInteger(to)) return { error: "scale.badRange" };
  const points = to - from + 1;
  // Under two there is nothing to average; over eleven it is a slider, which is a different
  // mechanic and a different story. Judged here so a bad pair is a message on THIS screen.
  if (points < 2 || points > SCALE_MAX_POINTS) return { error: "scale.badRange" };
  return {
    id: uid(),
    type: "mc",
    prompt: ask,
    aggregate: "mean",
    options: Array.from({ length: points }, (_, i) => ({ id: `o${i}`, label: String(from + i), value: from + i })),
  };
}

/**
 * "📊 Escala" — a check-in thermometer or a satisfaction pulse, whose result is the AVERAGE.
 *
 * A dedicated panel rather than a toggle on the preset openers above, and that is the load-bearing
 * choice: those presets answer a 400 through `asRoundRefusal`'s hard whitelist, which renders
 * NOTHING for a code it has not been told about (the round-1 lesson of US-LV-20). Here the refusal
 * goes through `refusalText`, which degrades to the code itself — so a rule the server adds
 * tomorrow still reaches the host as something.
 *
 * An `mc` opener like the rest, so it consumes the armed floor and the armed brief exactly as they
 * do and clears them only once the round actually went out. A brief carrying OPTIONS is impossible
 * from this panel and refused by the API anyway (`VARIANT_OPTIONS_ON_MEAN`): a scale is one table
 * for the room, and the phones' spoken mean reads the table their `question` frame delivered.
 */
function ScalePanel({ sessionId, armed, onResult, onSent }: {
  sessionId: string;
  armed: { eligible: string[]; targetPids: string[]; brief: string };
  onResult: (res: { status: number; data: unknown }) => void;
  onSent: (sent: { eligible: boolean; variants: boolean }) => void;
}): React.ReactElement {
  const [prompt, setPrompt] = React.useState(() => t("scale.presetPrompt"));
  const [from, setFrom] = React.useState<number>(1);
  const [to, setTo] = React.useState<number>(5);
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const open = async (): Promise<void> => {
    const built = scaleInput(prompt, from, to);
    if ("error" in built) return setErr(t(built.error));
    const targeting = variantFrom(armed.targetPids, armed.brief);
    if ("error" in targeting) return setErr(t(targeting.error));
    let config = built;
    if (armed.eligible.length > 0) config = { ...config, eligible: [...armed.eligible] };
    if (targeting.variants) config = { ...config, variants: targeting.variants };
    setErr(null);
    setBusy(true);
    const res = await api.openQuestion(sessionId, config);
    setBusy(false);
    setErr(
      res.status === 400
        ? t("scale.failed", { code: refusalText((res.data as { error?: string } | null)?.error, "scale") })
        : null,
    );
    onResult(res);
    if (res.status === 201) {
      onSent({ eligible: config.eligible !== undefined, variants: config.variants !== undefined });
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <h3 className="font-display text-display-sm">📊 {t("scale.title")}</h3>
      <p className="text-sm text-muted">{t("scale.hint")}</p>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("scale.prompt")}</span>
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          aria-label={t("scale.prompt")}
          className="rounded-md border border-border p-2"
        />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-sm">
          <span className="text-muted">{t("scale.from")}</span>
          <select
            value={from}
            onChange={(e) => setFrom(Number(e.target.value))}
            aria-label={t("scale.from")}
            className="rounded-md border border-border bg-surface px-2 py-1"
          >
            {SCALE_FROM.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-sm">
          <span className="text-muted">{t("scale.to")}</span>
          <select
            value={to}
            onChange={(e) => setTo(Number(e.target.value))}
            aria-label={t("scale.to")}
            className="rounded-md border border-border bg-surface px-2 py-1"
          >
            {SCALE_TO.map((v) => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </label>
        <p className="text-sm text-muted">{t("scale.points", { n: String(to - from + 1) })}</p>
      </div>
      {err && (
        <p role="alert" className="text-sm font-bold text-accent">
          {err}
        </p>
      )}
      <button
        onClick={() => void open()}
        disabled={busy}
        className="rounded-md bg-surface p-3 font-bold disabled:opacity-50"
      >
        {t("scale.open")}
      </button>
    </div>
  );
}

/**
 * The four source types that produce a ranked list of ANSWERS (W8a, US-LV-15).
 *
 * `picker` is absent and stays absent: a draw's result is a person the room was told to listen to,
 * and a ballot of people is not a round. The API refuses it too — this only decides whether the
 * control is offered.
 */
const DERIVABLE: ReadonlyArray<QuestionType> = ["mc", "promptvote", "open", "wordcloud"];

/** The finals the panel offers. `0` is untimed, which is what a poll already is. */
const FINAL_TIMES = [0, 20_000, 30_000, 60_000] as const;

/**
 * Is there a finished round to build a final out of?
 *
 * Pure and exported for the same reason `pickerInput` and `variantFrom` are: this is AC5's boundary
 * on the client side, and it must not need a DOM to test. The `promptvote` clause is not redundant
 * belt-and-braces — reveal IS the phase that holds the votes, so it is the phase this reads.
 */
export function canDerive(type: QuestionType, closed: boolean, aggregate: QuestionAggregate | null): boolean {
  if (!DERIVABLE.includes(type)) return false;
  return closed || (aggregate?.type === "promptvote" && aggregate.phase === "reveal");
}

/**
 * What the Final panel opens with, per source type.
 *
 * An `mc` run-off is a SECOND TURN between the top two — a run-off over three options is the same
 * poll again with fewer choices. Everything else is a shortlist, where four fits a phone without
 * becoming the original round back.
 */
export function deriveDefaults(sourceType: QuestionType): {
  k: number;
  promptKey: "derive.promptRunoff" | "derive.promptFinal" | "derive.promptWords";
} {
  if (sourceType === "mc") return { k: 2, promptKey: "derive.promptRunoff" };
  if (sourceType === "wordcloud") return { k: 4, promptKey: "derive.promptWords" };
  return { k: 4, promptKey: "derive.promptFinal" };
}

/**
 * The Final's request, built from the panel's own boxes AND the two things the host armed elsewhere.
 *
 * The armed floor ("Quem responde") and the armed brief ("Alvo") belong to the NEXT round, and the
 * final IS the next round — so it consumes them exactly as the typed openers do. Dropping them here
 * would silently move them onto the round after the final while both panels still said they applied.
 *
 * Pure and exported for the same reason `variantFrom` is, and it composes with it rather than
 * repeating it: the brief's one refusal (too long) is that function's to give.
 */
export function deriveSpecFrom(
  base: { k: number; prompt: string; timeLimitMs: number },
  armed: { eligible: readonly string[]; targetPids: string[]; brief: string },
): { spec: DeriveSpec } | { error: "target.tooLong" } {
  const targeting = variantFrom(armed.targetPids, armed.brief);
  if ("error" in targeting) return targeting;
  const spec: DeriveSpec = { pick: "top", k: base.k, prompt: base.prompt.trim() };
  // Untimed is spelled by OMITTING the key, exactly as the API reads it.
  if (base.timeLimitMs > 0) spec.timeLimitMs = base.timeLimitMs;
  if (armed.eligible.length > 0) spec.eligible = [...armed.eligible];
  if (targeting.variants) spec.variants = targeting.variants;
  return { spec };
}

/**
 * A refusal code, localized when there is a sentence for it and shown raw when there is not.
 *
 * The fallback is the point: the server owns this vocabulary and can add to it, and a code with no
 * sentence yet must still reach the host as SOMETHING — a click that silently does nothing gets
 * clicked again, which is the failure the whole `err` line exists to end.
 *
 * `group` is the key prefix, because two panels now answer 400s the same way and a second copy of
 * this would be a second place for the fallback to go missing.
 */
function refusalText(code: string | undefined, group: "derive" | "prio" | "scale" | "hosted"): string {
  const raw = code ?? "400";
  const key = `${group}.e.${raw}`;
  const localized = t(key);
  return localized === key ? raw : localized;
}

/** A single-screen round holds 2..6 options: the host taps one with a thumb in front of a room. */
const HOSTED_MIN_OPTIONS = 2;
const HOSTED_MAX_OPTIONS = 6;

/**
 * The host's single-screen round, built from the panel's boxes (US-LV-22, "sem celular").
 *
 * Pure and exported like `scaleInput`: whether a round is openable at all is worth a test that
 * needs no DOM. One option per line; blanks dropped; repeated labels refused (two taps that mean
 * the same thing cannot both be "the room's choice"). Ids are positional (`o0..on`).
 */
export function hostedInput(
  prompt: string,
  optionsText: string,
): QuestionConfig | { error: "hosted.badPrompt" | "hosted.badOptions" } {
  const ask = prompt.trim();
  if (ask.length === 0) return { error: "hosted.badPrompt" };
  const labels = optionsText.split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
  if (labels.length < HOSTED_MIN_OPTIONS || labels.length > HOSTED_MAX_OPTIONS) return { error: "hosted.badOptions" };
  if (new Set(labels.map((l) => l.toLowerCase())).size !== labels.length) return { error: "hosted.badOptions" };
  return {
    id: uid(),
    type: "mc",
    prompt: ask,
    hosted: true,
    options: labels.map((label, i) => ({ id: `o${i}`, label })),
  };
}

/**
 * "🖥️ Tela única" — a round the room answers TOGETHER, on the projection, with no phones.
 *
 * Its refusals go through `refusalText` (like the scale panel), so a rule the server adds tomorrow
 * still reaches the host as something. It consumes no armed floor or brief: the API refuses both
 * next to `hosted`, and there is nobody to target.
 */
function HostedPanel({ sessionId, onResult }: {
  sessionId: string;
  onResult: (res: { status: number; data: unknown }) => void;
}): React.ReactElement {
  const [prompt, setPrompt] = React.useState("");
  const [optionsText, setOptionsText] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const open = async (): Promise<void> => {
    const built = hostedInput(prompt, optionsText);
    if ("error" in built) return setErr(t(built.error));
    setErr(null);
    setBusy(true);
    const res = await api.openQuestion(sessionId, built);
    setBusy(false);
    setErr(
      res.status === 400
        ? t("hosted.failed", { code: refusalText((res.data as { error?: string } | null)?.error, "hosted") })
        : null,
    );
    onResult(res);
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <h3 className="font-display text-display-sm">🖥️ {t("hosted.title")}</h3>
      <p className="text-sm text-muted">{t("hosted.hint")}</p>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("hosted.prompt")}</span>
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          aria-label={t("hosted.prompt")}
          className="rounded-md border border-border p-2"
        />
      </label>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("hosted.options")}</span>
        <textarea
          value={optionsText}
          onChange={(e) => setOptionsText(e.target.value)}
          aria-label={t("hosted.options")}
          rows={4}
          className="rounded-md border border-border p-2"
        />
      </label>
      {err && (
        <p role="alert" className="text-sm font-bold text-accent">
          {err}
        </p>
      )}
      <button onClick={() => void open()} disabled={busy} className="rounded-md bg-surface p-3 font-bold disabled:opacity-50">
        {t("hosted.open")}
      </button>
    </div>
  );
}

/**
 * The one-tap record of a single-screen round: each option closes the round WITH that choice. Shown
 * only while the hosted round is open. A refused tap (409) leaves the round open and says so.
 */
function HostedRecordBar({ sessionId, options }: { sessionId: string; options: McOption[] }): React.ReactElement {
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const record = async (optionId?: string): Promise<void> => {
    setBusy(true);
    const res = await api.closeQuestion(sessionId, optionId);
    setBusy(false);
    setErr(
      res.status >= 400
        ? t("hosted.recordFailed", { code: refusalText((res.data as { error?: string } | null)?.error, "hosted") })
        : null,
    );
  };
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <h3 className="font-display text-display-sm">🖥️ {t("hosted.recordTitle")}</h3>
      <div className="flex flex-col gap-2">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => void record(o.id)}
            disabled={busy}
            className="rounded-md bg-primary p-3 font-bold text-primary-foreground disabled:opacity-50"
          >
            {t("hosted.record", { label: o.label })}
          </button>
        ))}
        <button onClick={() => void record()} disabled={busy} className="rounded-md bg-surface p-2 text-sm font-bold disabled:opacity-50">
          {t("hosted.noChoice")}
        </button>
      </div>
      {err && (
        <p role="alert" className="text-sm font-bold text-accent">
          {err}
        </p>
      )}
    </div>
  );
}

/**
 * "🏁 Final" — open a round built from the one that just closed.
 *
 * Mounted with the source round's id as its key, so every round gets its own defaults: a host who
 * runs a poll and then a word cloud should not find the word cloud's final still offering
 * "segundo turno" over two options.
 */
function DerivePanel({ sessionId, sourceType, armed, onResult, onSent }: {
  sessionId: string;
  sourceType: QuestionType;
  /** What the host armed on the other panels for the next round — the final IS the next round. */
  armed: { eligible: string[]; targetPids: string[]; brief: string };
  onResult: (res: { status: number; data: unknown }) => void;
  onSent: (sent: { eligible: boolean; variants: boolean }) => void;
}): React.ReactElement {
  const defaults = deriveDefaults(sourceType);
  const [k, setK] = React.useState(defaults.k);
  const [prompt, setPrompt] = React.useState(() => t(defaults.promptKey));
  const [timeLimitMs, setTimeLimitMs] = React.useState<number>(0);
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const open = async (): Promise<void> => {
    const built = deriveSpecFrom({ k, prompt, timeLimitMs }, armed);
    // A brief too long to send is the host's own input, said here rather than left to a 400.
    if ("error" in built) return setErr(t(built.error));
    setErr(null);
    setBusy(true);
    const res = await api.deriveRound(sessionId, built.spec);
    setBusy(false);
    // A 400 is this round's own situation — the source is still open, it was a draw, there were
    // fewer than two answers. Say which; 402 and 409 are the parent's banners.
    setErr(res.status === 400 ? t("derive.failed", { code: refusalText(res.data.error, "derive") }) : null);
    onResult(res);
    // Clear EXACTLY what went out, and only once it did: leaving them armed would re-target the
    // round after this one, and clearing them on a refusal would lose a selection that never left.
    if (res.status === 201) {
      onSent({ eligible: built.spec.eligible !== undefined, variants: built.spec.variants !== undefined });
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-accent p-3">
      <h3 className="font-display text-display-sm">🏁 {t("derive.title")}</h3>
      <p className="text-sm text-muted">{t("derive.hint")}</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-sm">
          <span className="text-muted">{t("derive.k")}</span>
          <select
            value={k}
            onChange={(e) => setK(Number(e.target.value))}
            aria-label={t("derive.k")}
            className="rounded-md border border-border bg-surface px-2 py-1"
          >
            {[2, 3, 4, 5, 6].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-sm">
          <span className="text-muted">{t("derive.time")}</span>
          <select
            value={timeLimitMs}
            onChange={(e) => setTimeLimitMs(Number(e.target.value))}
            aria-label={t("derive.time")}
            className="rounded-md border border-border bg-surface px-2 py-1"
          >
            {FINAL_TIMES.map((ms) => (
              <option key={ms} value={ms}>
                {ms === 0 ? t("derive.timeOff") : t("derive.timeSeconds", { n: String(ms / 1000) })}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted">{t("derive.prompt")}</span>
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          aria-label={t("derive.prompt")}
          className="rounded-md border border-border p-2"
        />
      </label>
      {err && (
        <p role="alert" className="text-sm font-bold text-accent">
          {err}
        </p>
      )}
      {/* An empty prompt is the one refusal this screen can prevent instead of report. */}
      <button
        onClick={() => void open()}
        disabled={busy || prompt.trim().length === 0}
        className="rounded-md bg-accent p-3 font-bold text-primary-foreground disabled:opacity-50"
      >
        {t("derive.open")}
      </button>
    </div>
  );
}

function presetQuestion(kind: "poll" | "quiz" | "wordcloud" | "qa" | "promptvote", preModerate = false, scoring?: ScoringConfig): QuestionConfig {
  if (kind === "promptvote") {
    // Both phases timed, so the server runs the round even if the host steps away.
    return {
      id: uid(),
      type: "promptvote",
      prompt: t("pv.presetPrompt"),
      timeLimitMs: 45_000,
      voteTimeLimitMs: 30_000,
    };
  }
  const id = uid();
  if (kind === "poll") {
    return { id, type: "mc", prompt: "Qual opção você prefere?", options: [
      { id: "a", label: "Opção A" }, { id: "b", label: "Opção B" }, { id: "c", label: "Opção C" },
    ] };
  }
  if (kind === "quiz") {
    const quiz: QuestionConfig = {
      id, type: "mc", prompt: "Quanto é 2 + 2?", scored: true, correctOptionIds: ["b"], timeLimitMs: 20_000,
      options: [{ id: "a", label: "3" }, { id: "b", label: "4" }, { id: "c", label: "5" }],
    };
    return scoring ? withScoring(quiz, scoring) : quiz;
  }
  if (kind === "wordcloud") {
    return { id, type: "wordcloud", prompt: "Uma palavra sobre hoje?", locale: getLocale() };
  }
  return { id, type: "open", prompt: "Perguntas para o facilitador?", allowUpvote: true, preModerate };
}

/**
 * Above this many participants, a Q&A round opens with the review gate ON by default.
 *
 * The threshold lives HERE and not in the runtime on purpose: it is a sensible default for the host
 * who is about to project free text, not an invariant the server should silently enforce behind
 * their back. The runtime does exactly what the round config says.
 */
const PRE_MODERATE_ABOVE = 50;

/**
 * The windows a long-running round can be opened with, in ms — plus 0, "eu fecho" (W11, US-LV-19).
 *
 * Day-scale beside the seconds-scale presets, because a homework round is not a longer poll: the
 * whole point is that it closes on Friday at 18:00 with nobody in the room. Only offered for the
 * three types whose answers survive an eviction (`mc`/`wordcloud`/`open`) — a prompt-vote round
 * mints its ballot deadline at the phase flip, so a three-day write phase would open a 30-second
 * vote at 03:00.
 */
const ASYNC_WINDOWS = [0, 24 * 3600_000, 72 * 3600_000, 168 * 3600_000] as const;

/**
 * The per-round response dials the host arms for the NEXT round (US-LV-20 AC7).
 *
 * One state bag rather than four `useState`s, and it lives beside `roundWindow` and `scoring` for
 * the same reason those do: the preset openers below are what send it, so it cannot live inside the
 * form that draws it.
 *
 * Each field is the ABSENT-IS-TODAY spelling of its dial: `maxWords: 0` means unlimited (the key is
 * omitted from the config), `showLive: true` means `revealTally` is omitted too. So a host who
 * touches none of these opens exactly the round the previous wave opened.
 */
interface Dials {
  maxWords: 0 | 1 | 2 | 3;
  showLive: boolean;
  oneEntry: boolean;
  named: boolean;
}

const NO_DIALS: Dials = { maxWords: 0, showLive: true, oneEntry: false, named: false };

/**
 * The three round types `revealTally` applies to — the API refuses it on the other two with
 * `INVALID_REVEAL_TALLY` (a prompt-vote round's PHASES are its withholding; a draw has no tally).
 */
const WITHHOLDABLE = new Set<QuestionConfig["type"]>(["mc", "wordcloud", "open"]);

/**
 * Merge the armed dials onto a preset config — only the ones that belong to ITS type (AC7/AC8).
 *
 * Exported and pure because this is the rule the API then re-judges with a 400: a dial on the wrong
 * type is `INVALID_MAX_WORDS` / `INVALID_PANEL` / `INVALID_REVEAL_TALLY`, so the one place that
 * decides which dials reach which round is worth asserting without a browser.
 *
 * "Mostrar ao vivo" reaches `mc` TOO (round-1 review, item H). It was skipping it, which made the
 * one checkbox on the panel that reads as global a no-op on the commonest round in the product: the
 * host unticked it, opened a poll, and the bars grew live anyway. `mc` has honoured `revealTally`
 * since W3 — the server needed nothing, the merge was simply not sending it.
 */
export function withDials(config: QuestionConfig, dials: Dials): QuestionConfig {
  // Merged once instead of per branch, so the checkbox cannot end up wired to two of three types
  // again. `as const` because `exactOptionalPropertyTypes` takes `false`, not `boolean`.
  const withheld = !dials.showLive && WITHHOLDABLE.has(config.type) ? { revealTally: false as const } : {};
  if (config.type === "wordcloud") {
    // `!== 0` rather than `> 0`, so the literal union narrows to `1 | 2 | 3` — with
    // `exactOptionalPropertyTypes` the wider `0 | 1 | 2 | 3` is not assignable to the field, and a
    // cast would hide exactly the mistake the union is there to catch.
    const { maxWords } = dials;
    return { ...config, ...(maxWords !== 0 ? { maxWords } : {}), ...withheld };
  }
  if (config.type === "open") {
    return {
      ...config,
      ...(dials.oneEntry ? { maxEntries: 1 as const } : {}),
      ...(dials.named ? { named: true } : {}),
      ...withheld,
    };
  }
  if (config.type === "mc") return { ...config, ...withheld };
  return config;
}

/**
 * The two dial groups, each under its own type's name (AC7: "each type shows only its own dials").
 *
 * The openers on this screen are PRESET BUTTONS, not a type picker, so there is no "selected type"
 * to switch a single form on. Grouping by heading is what makes the mapping unambiguous instead:
 * what is under "nuvem" reaches a word cloud and nothing else, and `withDials` above enforces that
 * rather than trusting the layout.
 */
function DialsPanel({ dials, setDials }: { dials: Dials; setDials: (d: Dials) => void }): React.ReactElement {
  return (
    <div className="flex flex-col gap-3 rounded-md bg-surface p-4">
      <h2 className="font-bold">{t("presenter.roundOptions")}</h2>
      <fieldset className="flex flex-wrap items-center gap-3 text-sm">
        <legend className="font-bold">{t("presenter.wordcloud")}</legend>
        <label className="flex items-center gap-2">
          <span>{t("presenter.wordsPerPerson")}</span>
          <select
            value={dials.maxWords}
            onChange={(e) => setDials({ ...dials, maxWords: Number(e.target.value) as Dials["maxWords"] })}
            className="rounded-md border border-border bg-surface px-2 py-1"
          >
            <option value={0}>{t("presenter.wordsUnlimited")}</option>
            <option value={1}>1</option>
            <option value={2}>2</option>
            <option value={3}>3</option>
          </select>
        </label>
      </fieldset>
      <fieldset className="flex flex-wrap items-center gap-4 text-sm">
        <legend className="font-bold">{t("presenter.qa")}</legend>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={dials.oneEntry} onChange={(e) => setDials({ ...dials, oneEntry: e.target.checked })} />
          <span>{t("presenter.oneEntryPerPerson")}</span>
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={dials.named} onChange={(e) => setDials({ ...dials, named: e.target.checked })} />
          <span>{t("presenter.namedPanel")}</span>
        </label>
      </fieldset>
      {/* The host's half of the disclosure. The participant's half is a line on their own phone
          before they submit — this one exists so the facilitator knows that, before they tick it. */}
      {dials.named && <p className="text-body-sm text-muted">{t("presenter.namedPanelHint")}</p>}
      {/* Outside both groups because it is the same dial with the same meaning on three types —
          and the caption NAMES them, so a host does not read a checkbox with no heading as global
          and expect it on a draw (round-1 review, item H). */}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={dials.showLive} onChange={(e) => setDials({ ...dials, showLive: e.target.checked })} />
        <span>{t("presenter.showLive")}</span>
      </label>
      <p className="text-body-sm text-muted">{t("presenter.showLiveTypes")}</p>
    </div>
  );
}

export function Presenter(): React.ReactElement {
  const [session, setSession] = React.useState<{
    sessionId: string;
    joinCode: string;
    joinUrl: string;
    expiresAt: number;
    longRunning: boolean;
    seriesId: string | null;
  } | null>(null);
  const [creating, setCreating] = React.useState(false);
  // "Uma sessão que dura semanas" (W11): asked at creation, because the horizon is stamped on the
  // row and nothing after this can change it.
  const [longRunning, setLongRunning] = React.useState(false);
  // The host's own label for a recurring meeting (W13, US-LV-23): the same name each time is what
  // lets the trend screen put the sessions on one line. Optional; an invalid one is not sent.
  const [seriesInput, setSeriesInput] = React.useState("");
  // The window the NEXT round gets. Lives here beside the scoring mode and for the same reason:
  // the openers below are what send it.
  const [roundWindow, setRoundWindow] = React.useState(0);
  // ...and the per-type response dials, for exactly the same reason (US-LV-20 AC7).
  const [dials, setDials] = React.useState<Dials>(NO_DIALS);
  const [refused, setRefused] = React.useState<string | null>(null);
  // Lifted so a trivia race can run the same AI-generated drafts the host reviewed.
  const [drafts, setDrafts] = React.useState<QuestionConfig[]>([]);
  const [limit, setLimit] = React.useState<LimitError | null>(null);
  const [over, setOver] = React.useState<"closed" | "expired" | null>(null);
  // Corporate/training default: rank by what people know, not by their reflexes. `speed` is the
  // party opt-in; `self-paced` drops the clock for rooms with screen readers or slow readers.
  const [scoring, setScoring] = React.useState<ScoringConfig>({ mode: "accuracy" });
  // The pending targeting slice (W6a): who gets a private version of the NEXT round, and what it
  // says. Lives here rather than in the panel because the openers below are what send it.
  const [targetPids, setTargetPids] = React.useState<string[]>([]);
  const [brief, setBrief] = React.useState("");
  const [targetError, setTargetError] = React.useState<string | null>(null);
  // Who may answer the NEXT round (W7). Lifted beside the targeting slice and for the same reason:
  // the openers below are what send it, and what must clear it once it has actually gone out.
  const [eligible, setEligible] = React.useState<string[]>([]);
  // `observer`: this screen watches and never answers, so it must not touch the offline queue a
  // PARTICIPANT in the same browser stores under the identical `connectPath`.
  const { state } = useRoom(session ? `/sessions/${session.sessionId}/connect` : null, { observer: true });

  const create = async (): Promise<void> => {
    setCreating(true);
    const { status, data } = await api.createSession({ longRunning, seriesId: seriesInput });
    setCreating(false);
    // Before this, a refused create left the button enabled and nothing on screen — the host was
    // told nothing at all and would click again. A 402 ASYNC_NOT_ON_PLAN lands here too (W11).
    setLimit(asLimitError(status, data));
    if (status === 201) {
      setSession({
        sessionId: data.sessionId,
        joinCode: data.joinCode,
        joinUrl: data.joinUrl,
        expiresAt: data.expiresAt,
        longRunning,
        seriesId: normalizeSeriesId(seriesInput),
      });
      setOver(null); // a fresh room is not the old room's ending
    }
  };
  const open = async (kind: "poll" | "quiz" | "wordcloud" | "qa" | "promptvote"): Promise<void> => {
    if (!session) return;
    let config = presetQuestion(kind, state.participants > PRE_MODERATE_ABOVE, scoring);
    // Only mc and open carry a targeting table — a word cloud has nothing to vary, and a
    // prompt-vote ballot is a room-wide artefact that per-person content corrupts. The panel is
    // judged ONLY for those two: a word cloud must neither fail on a too-long brief nor clear a
    // brief it never sent (the host would take the reset as delivery).
    let sent = false;
    // The floor applies to every type that takes an answer — which is every preset here, since a
    // `picker` is opened by its own panel and the API refuses `eligible` on one anyway.
    if (eligible.length > 0) config = { ...config, eligible };
    // The wall-clock window, for the three types whose answers survive an eviction. Replaces the
    // preset's own seconds-scale limit rather than adding to it — a round has one deadline.
    if (roundWindow > 0 && (config.type === "mc" || config.type === "wordcloud" || config.type === "open")) {
      config = { ...config, timeLimitMs: roundWindow };
    }
    // The armed dials, only the ones this type takes (US-LV-20 AC7). Unlike the targeting slice and
    // the eligibility floor, these are NOT cleared after a successful open: "one word per person"
    // and "painel com nome" are how this facilitator runs their room, not a one-round instruction,
    // and re-ticking them every round is the kind of friction that makes a dial go unused.
    config = withDials(config, dials);
    if (config.type === "mc" || config.type === "open") {
      const targeting = variantFrom(targetPids, brief);
      // A brief too long to send is the host's own input; say so here rather than let the API
      // answer with a 400 that nothing on this screen renders.
      if ("error" in targeting) return setTargetError(t(targeting.error));
      setTargetError(null);
      if (targeting.variants) {
        config = { ...config, variants: targeting.variants };
        sent = true;
      }
    }
    const { status, data } = await api.openQuestion(session.sessionId, config);
    // Same silence on the round quota: the 4th round on a free plan simply never appeared.
    setLimit(asLimitError(status, data));
    // ...and the same silence on a window the session cannot honour (W11): a 400 nothing recognises
    // renders nothing, so the host clicks "abrir rodada" and the screen does not move.
    setRefused(asRoundRefusal(status, data));
    // ...and the same silence again when the room itself has ended, which the server now says
    // rather than accepting a round into a session nobody can reach.
    setOver(asSessionOver(status, data));
    // The slice belongs to the round it was sent with. Leaving it armed would quietly re-target the
    // NEXT round at the same people, which is exactly the kind of thing a facilitator finds out
    // about from a participant.
    if (status === 201 && sent) {
      setTargetPids([]);
      setBrief("");
    }
    // Same rule for the floor, separately: it is armed independently of a brief, and leaving it armed
    // would silently mute the same people for the next round too.
    if (status === 201 && eligible.length > 0) setEligible([]);
  };

  return (
    <Shell>
      <h1 className="font-display text-display-md">{t("presenter.title")}</h1>
      {limit && <LimitBanner limit={limit} />}
      {over && <SessionOverNotice reason={over} />}
      {refused && (
        <p role="alert" className="rounded-lg border border-warning p-4 text-body-sm">
          {t(`error.${refused}`)}
        </p>
      )}
      <LiveRegion
        text={
          state.leaderboard.length > 0 || state.teamBoard.length > 0
            ? describeLeaderboard(state.leaderboard, state.teamBoard)
            : describeAggregate(state.aggregate, {
                options: state.question?.options ?? [],
                question: state.question,
                closed: state.closed,
              })
        }
      />

      {!session && (
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={longRunning} onChange={(e) => setLongRunning(e.target.checked)} />
            <span className="font-bold">{t("presenter.longRunning")}</span>
          </label>
          <p className="text-body-sm text-muted">{t("presenter.longRunningHint")}</p>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-bold">{t("presenter.series")}</span>
            <input
              value={seriesInput}
              onChange={(e) => setSeriesInput(e.target.value)}
              maxLength={64}
              aria-invalid={!isSeriesIdValid(seriesInput)}
              className="max-w-xs rounded-md border px-3 py-2"
            />
          </label>
          <p className="text-body-sm text-muted">
            {t(isSeriesIdValid(seriesInput) ? "presenter.seriesHint" : "presenter.seriesInvalid")}
          </p>
          <button
            onClick={() => void create()}
            disabled={creating}
            className="self-start rounded-md bg-primary px-6 py-3 font-bold text-primary-foreground disabled:opacity-50"
          >
            {t("presenter.create")}
          </button>
        </div>
      )}

      {session && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-md bg-surface p-4">
            {/* Code, URL and QR go together or not at all (G2): `endRace()` closes the room with
                no button and never touches D1, so a latecomer typing the code off the host's screen
                RESOLVES the session and is then refused the seat — printing the code after the room
                is over is the same broken promise as showing the QR. `SessionOverNotice` already
                stands above this. The `"host"` surface stays visible while the socket is merely
                connecting or dead: the session was created `live` by the call that returned this
                code, so a blocked WebSocket upgrade must not cost the host their QR (G1).

                The QR encodes `session.joinUrl` (the API's own PARTICIPANT_BASE_URL), NOT
                `location.origin`: a host on localhost or a Pages preview branch would otherwise
                hand the room a QR pointing at an origin no phone can reach. */}
            {advertisableCode(state.status, session.joinCode, "host") !== "" && (
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-sm">{t("presenter.code")}</div>
                  <div className="font-display text-display-md tracking-widest">{session.joinCode}</div>
                  <div className="text-sm text-muted">{session.joinUrl.replace(/^https?:\/\//, "")}</div>
                </div>
                <QrSvg
                  url={session.joinUrl}
                  label={t("join.qrLabel", { code: session.joinCode })}
                  className="h-24 w-24"
                />
              </div>
            )}
            <div className="text-right">
              <div className="text-sm">{t("presenter.participants")}</div>
              <div className="font-display text-display-md">{state.participants}</div>
              {session.seriesId !== null && (
                <div className="text-sm">
                  <Link to={`/host/serie/${encodeURIComponent(session.seriesId)}`} className="underline">
                    {t("series.link")}
                  </Link>
                </div>
              )}
              {/* How long the link the host is about to hand a cohort actually lasts (W11). */}
              {session.longRunning && closesOn(session.expiresAt * 1000, getLocale()) !== null && (
                <div className="text-sm text-muted">
                  {t("presenter.horizon", { date: closesOn(session.expiresAt * 1000, getLocale()) ?? "" })}
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link className="text-accent underline" to={`/projecao/${session.sessionId}`} target="_blank" rel="noreferrer">
              {t("presenter.projection")} →
            </Link>
            {/* The only way into the results page. Without this the durable metrics exist and
                nobody can reach them. */}
            <Link className="text-accent underline" to={`/resultados/${session.sessionId}`} target="_blank" rel="noreferrer">
              {t("results.title")} →
            </Link>
          </div>

          <label className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-bold">{t("presenter.scoring")}</span>
            <select
              value={scoring.mode}
              onChange={(e) => setScoring({ mode: e.target.value as ScoringMode })}
              className="rounded-md border border-border bg-surface px-2 py-1"
            >
              <option value="accuracy">{t("presenter.scoringAccuracy")}</option>
              <option value="speed">{t("presenter.scoringSpeed")}</option>
              <option value="self-paced">{t("presenter.scoringSelfPaced")}</option>
            </select>
          </label>

          {/* Two integers this socket already carries: the seat count in `state` and the round's own
              response count in the aggregate. Never a list of who is missing — the room is anonymous,
              and "quem falta" is a name the product does not have. In a long-running room the seat is
              KEPT on disconnect, which is exactly what makes `participants` mean *enrolled*. */}
          {session.longRunning && state.question && !state.closed && answeredIn(state.aggregate) !== null && (
            <p className="text-sm font-bold">
              {t("presenter.attendance", {
                answered: String(answeredIn(state.aggregate) ?? 0),
                enrolled: String(state.participants),
              })}
            </p>
          )}

          {session.longRunning && (
            <label className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-bold">{t("presenter.roundWindow")}</span>
              <select
                value={roundWindow}
                onChange={(e) => setRoundWindow(Number(e.target.value))}
                className="rounded-md border border-border bg-surface px-2 py-1"
              >
                {/* Only the windows that FIT: a 7-day round opened in a 7-day session is refused by
                    the server (`ROUND_OUTLIVES_SESSION`), and offering a choice that always fails is
                    a trap, not an option. */}
                {ASYNC_WINDOWS.filter((ms) => ms === 0 || session.expiresAt * 1000 - Date.now() > ms).map((ms) => (
                  <option key={ms} value={ms}>
                    {ms === 0
                      ? t("presenter.windowOff")
                      : ms < 48 * 3600_000
                        ? t("presenter.windowHours", { n: String(ms / 3600_000) })
                        : t("presenter.windowDays", { n: String(ms / (24 * 3600_000)) })}
                  </option>
                ))}
              </select>
            </label>
          )}

          <DialsPanel dials={dials} setDials={setDials} />

          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => void open("poll")} className="rounded-md bg-surface p-3 font-bold">{t("presenter.open")}: {t("presenter.poll")}</button>
            <button onClick={() => void open("quiz")} className="rounded-md bg-surface p-3 font-bold">{t("presenter.open")}: {t("presenter.quiz")}</button>
            <button onClick={() => void open("wordcloud")} className="rounded-md bg-surface p-3 font-bold">{t("presenter.open")}: {t("presenter.wordcloud")}</button>
            <button onClick={() => void open("qa")} className="rounded-md bg-surface p-3 font-bold">{t("presenter.open")}: {t("presenter.qa")}</button>
            <button onClick={() => void open("promptvote")} className="col-span-2 rounded-md bg-accent p-3 font-bold text-primary-foreground">
              {t("presenter.open")}: {t("pv.open")}
            </button>
          </div>

          <TargetPanel
            sessionId={session.sessionId}
            headcount={state.participants}
            pids={targetPids}
            setPids={setTargetPids}
            brief={brief}
            setBrief={setBrief}
            error={targetError}
          />

          <TeamsPanel
            sessionId={session.sessionId}
            headcount={state.participants}
            standings={state.teamBoard}
            eligible={eligible}
            setEligible={setEligible}
          />

          <PickerPanel
            sessionId={session.sessionId}
            aggregate={state.aggregate?.type === "picker" ? state.aggregate : null}
            onResult={({ status, data }) => {
              // Same two silences the other openers now answer: a draw is a round, so it can hit the
              // free plan's quota or land in a room that has already ended.
              setLimit(asLimitError(status, data));
              setOver(asSessionOver(status, data));
            }}
          />

          <PriorityPanel
            sessionId={session.sessionId}
            armed={{ eligible, targetPids, brief }}
            onResult={({ status, data }) => {
              setLimit(asLimitError(status, data));
              setOver(asSessionOver(status, data));
            }}
            onSent={(sent) => {
              if (sent.eligible) setEligible([]);
              if (sent.variants) {
                setTargetPids([]);
                setBrief("");
              }
            }}
          />

          <ScalePanel
            sessionId={session.sessionId}
            armed={{ eligible, targetPids, brief }}
            onResult={({ status, data }) => {
              setLimit(asLimitError(status, data));
              setOver(asSessionOver(status, data));
            }}
            onSent={(sent) => {
              if (sent.eligible) setEligible([]);
              if (sent.variants) {
                setTargetPids([]);
                setBrief("");
              }
            }}
          />

          <HostedPanel
            sessionId={session.sessionId}
            onResult={({ status, data }) => {
              setLimit(asLimitError(status, data));
              setOver(asSessionOver(status, data));
            }}
          />

          <AiDraftPanel sessionId={session.sessionId} drafts={drafts} setDrafts={setDrafts} scoring={scoring} />

          <RacePanel sessionId={session.sessionId} race={state.race} drafts={drafts} scoring={scoring} />

          {state.question?.hosted === true && !state.closed && state.question.options && (
            <HostedRecordBar key={state.question.id} sessionId={session.sessionId} options={state.question.options} />
          )}

          {state.question && (
            <div className="flex gap-2">
              <button onClick={() => void api.closeQuestion(session.sessionId)} className="flex-1 rounded-md bg-primary p-3 font-bold text-primary-foreground">
                {t("presenter.close")}
              </button>
              <button onClick={() => void api.revealQuestion(session.sessionId)} className="flex-1 rounded-md bg-secondary p-3 font-bold text-secondary-foreground">
                {t("presenter.reveal")}
              </button>
            </div>
          )}

          {state.question && canDerive(state.question.type, state.closed, state.aggregate) && (
            <DerivePanel
              // Keyed by the SOURCE round, so each round's final starts from its own defaults.
              key={state.question.id}
              sessionId={session.sessionId}
              sourceType={state.question.type}
              armed={{ eligible, targetPids, brief }}
              onResult={({ status, data }) => {
                // A final is a round: it takes quota, and it lands in a room that may have ended.
                setLimit(asLimitError(status, data));
                setOver(asSessionOver(status, data));
              }}
              onSent={(sent) => {
                if (sent.eligible) setEligible([]);
                if (sent.variants) {
                  setTargetPids([]);
                  setBrief("");
                }
              }}
            />
          )}

          {state.question && state.aggregate?.type === "mc" && (
            <McPanel prompt={state.question.prompt} options={state.question.options ?? []} agg={state.aggregate} />
          )}
          {/* Withheld from the HOST's broadcast too, exactly as `mc` is — see McPanel's note. The
              host's own moderation read (`ReviewQueue`) is authenticated and unaffected. */}
          <WithheldNote question={state.question} closed={state.closed} aggregate={state.aggregate} />
          {state.aggregate?.type === "wordcloud" && (
            <div className="flex flex-wrap gap-2">
              {state.aggregate.words.map((w) => (
                <span key={w.word} className="rounded-md bg-surface px-2 py-1" style={{ fontSize: `${1 + Math.min(w.count, 6) * 0.25}rem` }}>
                  {w.word}
                </span>
              ))}
            </div>
          )}
          {state.aggregate?.type === "promptvote" && (
            <PromptVoteHostPanel sessionId={session.sessionId} aggregate={state.aggregate} />
          )}
          {state.aggregate?.type === "open" && (
            <ReviewQueue sessionId={session.sessionId} pendingCount={state.aggregate.pendingCount} />
          )}
          {state.aggregate?.type === "open" && (
            <GroupPanel sessionId={session.sessionId} entries={state.aggregate.entries} />
          )}
          {state.aggregate?.type === "open" && (
            <div className="flex flex-col gap-1">
              {state.aggregate.entries.map((e) => (
                <div key={e.id} className={`rounded-md p-2 ${e.highlighted ? "bg-success text-primary-foreground" : "bg-surface"}`}>
                  {e.highlighted && <span aria-hidden="true">{"\u2605 "}</span>}
                  ▲ {e.upvotes} — <OpenEntryText entry={e} />
                  {e.highlighted && <span className="sr-only"> ({t("a11y.highlighted")})</span>}
                </div>
              ))}
            </div>
          )}
          {state.leaderboard.length > 0 && (
            <div className="flex flex-col gap-1">
              <h3 className="font-display text-display-sm">{t("common.leaderboard")}</h3>
              {state.leaderboard.slice(0, 10).map((e, i) => (
                <div key={i} className="flex justify-between rounded-md bg-surface p-2">
                  <span>{e.rank}. {e.nickname}</span>
                  <span className="font-bold">{e.score}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Shell>
  );
}
